// ============================================================================
// CHANGO V5.0 — "ops" SPA entry (Task #53)
// ============================================================================
// Platform admin console. Served at ops.chango.local for all paths.
//
// All routes here require `admin` role (enforced by RoleGate on every Route,
// and again by the server on every /api/ops/* endpoint — see §20 #21).
//
// Routes:
//   /ops/login
//   /ops/dashboard
//   /ops/review-requests       — double-review inbox
//   /ops/sku-pricing           — platform product pricing
//   /ops/admin-seats           — grant/revoke admin role
//   /ops/mail-inbound          — email reply inbox
//   /ops/legal-agreements      — TOS / privacy / billing terms upload
//   /ops/system-config         — billing mode, trial length, archive threshold
//   /ops/archived-workspaces   — soft-archive ledger
//   /ops/cms/pages             — marketing page editor (Task #51-52)
//   /ops/cms/pages/:slug       — per-page editor
//   /ops/cms/notices           — site-wide notice editor
//
// Explicitly NOT in this bundle:
//   • Tenant workspace (/app/*)          — app bundle
//   • Public capability URLs             — public bundle
//   • Marketing site                     — site bundle
//
// Nginx is configured so ops.chango.local/ redirects to /ops/login.
// ============================================================================

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import '../styles/tailwind.css';

import { initTelemetry, captureError } from '../lib/telemetry.js';
initTelemetry().catch(() => { /* no-op */ });

if (typeof window !== 'undefined') {
  window.addEventListener('error', (ev) => {
    try { captureError(ev.error || new Error(ev.message || 'window.error'), { kind: 'window.error', spa: 'ops' }); } catch {}
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev?.reason;
    try { captureError(reason instanceof Error ? reason : new Error(String(reason)), { kind: 'unhandledrejection', spa: 'ops' }); } catch {}
  });
}

import { AuthProvider } from '../context/AuthContext.jsx';
import RequireAuth from '../components/RequireAuth.jsx';
import RoleGate from '../components/RoleGate.jsx';

import OpsLogin from '../pages/ops/OpsLogin.jsx';
import OpsDashboard from '../pages/ops/OpsDashboard.jsx';
import OpsReviewInbox from '../pages/ops/OpsReviewInbox.jsx';
import OpsSkuPricing from '../pages/ops/OpsSkuPricing.jsx';
import OpsWorkspacePlan from '../pages/ops/OpsWorkspacePlan.jsx'; // §9.5.1 Round 13
import OpsAdminSeats from '../pages/ops/OpsAdminSeats.jsx';
import OpsMailInbound from '../pages/ops/OpsMailInbound.jsx';
import OpsLegalAgreements from '../pages/ops/OpsLegalAgreements.jsx';
import OpsSystemConfig from '../pages/ops/OpsSystemConfig.jsx';
import OpsArchivedWorkspaces from '../pages/ops/OpsArchivedWorkspaces.jsx';
import OpsAuditQueue from '../pages/ops/OpsAuditQueue.jsx'; // §9.6.2d 审核队列
import OpsAuditLog from '../pages/ops/OpsAuditLog.jsx';
import OpsCustomers from '../pages/ops/OpsCustomers.jsx';
import OpsCompanyNameChangeQueue from '../pages/ops/OpsCompanyNameChangeQueue.jsx'; // §9.6.2e 公司名变更队列
import OpsCmsPages from '../pages/ops/cms/OpsCmsPages.jsx';
import OpsCmsPageEditor from '../pages/ops/cms/OpsCmsPageEditor.jsx';
import OpsCmsNotices from '../pages/ops/cms/OpsCmsNotices.jsx';

import Forbidden from '../pages/Forbidden.jsx';
import NotFound from '../pages/NotFound.jsx';

// Every ops route gets the same triple-wrap. A small helper keeps the route
// table readable and makes "did I remember RoleGate?" impossible to miss.
//
// Phase 3 RoleGate-B (2026-04-24):
//   owner 是 admin 的严格超集；backend 发回来的 JWT role 可能是 'owner'
//   （平台所有者）或 'admin'（非 owner ops 席位）。两者都应进入 ops 控制台。
//   domain-native 细分（ops_users.role='ops'/'support'）由后端 opsRoleGuard
//   决定，前端只做粗粒度路由守卫。
function Admin({ children }) {
  return (
    <RequireAuth>
      <RoleGate allow={['admin', 'owner']} fallback={<Navigate to="/403" replace />}>
        {children}
      </RoleGate>
    </RequireAuth>
  );
}

function OpsApp() {
  return (
    <BrowserRouter>
      {/* `loginPath` tells RequireAuth where to send an unauthenticated user.
          Ops-only bundle has `/ops/login`, not a bare `/login`, so override
          the AuthProvider default. Task #55. */}
      <AuthProvider loginPath="/ops/login">
        <Routes>
          {/* Bare / on ops.chango.local → login. Nginx issues a 302 at the
              edge, but we belt-and-brace in-SPA too. */}
          <Route path="/"          element={<Navigate to="/ops/login" replace />} />
          <Route path="/ops"       element={<Navigate to="/ops/dashboard" replace />} />
          <Route path="/403"       element={<Forbidden />} />
          <Route path="/404"       element={<NotFound />} />

          <Route path="/ops/login" element={<OpsLogin />} />

          <Route path="/ops/dashboard"          element={<Admin><OpsDashboard /></Admin>} />
          <Route path="/ops/customers"          element={<Admin><OpsCustomers /></Admin>} />
          <Route path="/ops/review-requests"    element={<Admin><OpsReviewInbox /></Admin>} />
          <Route path="/ops/sku-pricing"        element={<Admin><OpsSkuPricing /></Admin>} />
          {/* §9.5.1 Round 13 — 套餐管理 (改客户 enabled_layers + sku_code) */}
          <Route path="/ops/workspaces/:id/plan" element={<Admin><OpsWorkspacePlan /></Admin>} />
          {/* §9.6.2d 审核队列 */}
          <Route path="/ops/workspaces/audit-queue" element={<Admin><OpsAuditQueue /></Admin>} />
          <Route path="/ops/audit-log" element={<Admin><OpsAuditLog /></Admin>} />
          {/* §9.6.2e 公司名变更审核队列 */}
          <Route path="/ops/company-name-changes" element={<Admin><OpsCompanyNameChangeQueue /></Admin>} />
          <Route path="/ops/admin-seats"        element={<Admin><OpsAdminSeats /></Admin>} />
          <Route path="/ops/mail-inbound"       element={<Admin><OpsMailInbound /></Admin>} />
          <Route path="/ops/legal-agreements"   element={<Admin><OpsLegalAgreements /></Admin>} />
          <Route path="/ops/system-config"      element={<Admin><OpsSystemConfig /></Admin>} />
          <Route path="/ops/archived-workspaces" element={<Admin><OpsArchivedWorkspaces /></Admin>} />
          <Route path="/ops/cms/pages"          element={<Admin><OpsCmsPages /></Admin>} />
          <Route path="/ops/cms/pages/:slug"    element={<Admin><OpsCmsPageEditor /></Admin>} />
          <Route path="/ops/cms/notices"        element={<Admin><OpsCmsNotices /></Admin>} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <OpsApp />
  </React.StrictMode>
);
